import VoiceActorSelector from "./VoiceActorSelector";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  TextField,
  Typography,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Grid2,
  Button,
  Box,
  Paper,
} from "@mui/material";
import PropTypes from "prop-types";
import AudioPlayerComponent from "./AudioPlayer";
import { useCallback } from "react";

export const questionTypes = [
  "MC",
  "MCM",
  "ACIQ",
  "ACMC",
  "ACIQM",
  "LAR",
  "UVI",
  "CTA",
  "CT",
];

export const QUESTION_TYPE_MAPPING = {
  "MC": "MC (Multiple Choice)",
  "MCM": "MCM (Multiple Choice Multi-answer)",
  "ACIQ": "ACIQ (Audio Clip In Question)",
  "ACMC": "ACMC (Audio Clip Multiple Choice)",
  "ACIQM": "ACIQM (Audio Clip In Question Multi)",
  "LAR": "LAR (Listen and Repeat)",
  "UVI": "UVI (User Voice Input)",
  "CTA": "CTA (Click Through Audio)",
  "CT": "CT (REMOVED - DO NOT USE)",
};

const hintTypes = ["none", "audio", "text"];

const SINGLE_ANSWER_TYPES = new Set(["MC", "ACIQ", "ACMC", "CTA"]);
const MULTI_ANSWER_TYPES = new Set(["MCM", "ACIQM"]);
const QUESTION_TYPES_WITH_OPTIONS = new Set([
  "MC",
  "MCM",
  "ACIQ",
  "ACMC",
  "ACIQM",
]);

const createEmptyOption = (type, index) => {
  const letter = String.fromCharCode(65 + index); // 65 is 'A'
  return type === "ACMC"
    ? {
        text: letter,
        correct: false,
        audioUrl: "",
        audioFile: null,
      }
    : { text: letter, correct: false };
};

const getInitialOptions = (type) => {
  if (!QUESTION_TYPES_WITH_OPTIONS.has(type)) {
    return [];
  }
  const defaultLength = type === "ACMC" ? 0 : 2;
  return Array.from({ length: defaultLength }, (_, optionIndex) =>
    createEmptyOption(type, optionIndex),
  );
};

const NewQuestionForm = forwardRef(function NewQuestionForm(
  {
    courseData,
    unitId,
    levelId,
    levelOrder,
    nextOrder,
    onCancel,
    onSuccess,
    onChange,
    displayConfirmButton,
    secondaryBtnText,
  },
  ref,
) {
  const [voiceName, setVoiceName] = useState("");
  const [questionType, setQuestionType] = useState("");
  const [question, setQuestion] = useState("");
  const [noteBrief, setNoteBrief] = useState("");
  const [noteFull, setNoteFull] = useState("");
  const [phrase, setPhrase] = useState("");
  const [hintType, setHintType] = useState("none");
  const [hint, setHint] = useState("");

  const [options, setOptions] = useState([]);
  const [questionAudio, setQuestionAudio] = useState({
    file: null,
    preview: "",
  });
  const [hintAudio, setHintAudio] = useState({ file: null, preview: "" });
  const [fieldErrors, setFieldErrors] = useState({});
  const [formMessage, setFormMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const questionAudioInputRef = useRef(null);
  const hintAudioInputRef = useRef(null);

  const handleAddOption = () => {
    setOptions((prev) => [
      ...prev,
      createEmptyOption(questionType, prev.length),
    ]);
  };

  const handleOptionTextChange = (index, value) => {
    setOptions((prev) =>
      prev.map((option, optionIndex) =>
        optionIndex === index ? { ...option, text: value } : option,
      ),
    );
  };

  const handleOptionCorrectChange = (index, checked) => {
    setOptions((prev) =>
      prev.map((option, optionIndex) => {
        if (optionIndex !== index) {
          if (SINGLE_ANSWER_TYPES.has(questionType)) {
            return { ...option, correct: false };
          }
          return option;
        }

        return { ...option, correct: checked };
      }),
    );
  };

  const handleRemoveOption = (index) => {
    setOptions((prev) => {
      const filtered = prev.filter((_, optionIndex) => optionIndex !== index);
      // For ACMC, regenerate option letters to maintain A, B, C sequence
      if (questionType === "ACMC") {
        return filtered.map((option, idx) => ({
          ...option,
          text: String.fromCharCode(65 + idx),
        }));
      }
      return filtered;
    });
  };

  const resetQuestionAudio = () => {
    setQuestionAudio((prev) => {
      if (prev.preview) {
        URL.revokeObjectURL(prev.preview);
      }
      return { file: null, preview: "" };
    });
    if (questionAudioInputRef.current) {
      questionAudioInputRef.current.value = "";
    }
  };

  const resetHintAudio = () => {
    setHintAudio((prev) => {
      if (prev.preview) {
        URL.revokeObjectURL(prev.preview);
      }
      return { file: null, preview: "" };
    });
    if (hintAudioInputRef.current) {
      hintAudioInputRef.current.value = "";
    }
  };

  const handleQuestionAudioChange = (file) => {
    setFieldErrors((prev) => ({ ...prev, questionAudio: undefined }));
    setFormMessage(null);
    setQuestionAudio((prev) => {
      if (prev.preview) {
        URL.revokeObjectURL(prev.preview);
      }
      if (!file) {
        return { file: null, preview: "" };
      }
      return { file, preview: URL.createObjectURL(file) };
    });
  };

  const handleHintAudioChange = (file) => {
    setFieldErrors((prev) => ({ ...prev, hintAudio: undefined }));
    setFormMessage(null);
    setHintAudio((prev) => {
      if (prev.preview) {
        URL.revokeObjectURL(prev.preview);
      }
      if (!file) {
        return { file: null, preview: "" };
      }
      return { file, preview: URL.createObjectURL(file) };
    });
  };

  const handleOptionAudioChange = (index, file) => {
    setOptions((prev) =>
      prev.map((option, optionIndex) => {
        if (optionIndex !== index) {
          return option;
        }
        if (option.audioUrl) {
          URL.revokeObjectURL(option.audioUrl);
        }
        if (!file) {
          return { ...option, audioFile: null, audioUrl: "" };
        }
        return {
          ...option,
          audioFile: file,
          audioUrl: URL.createObjectURL(file),
        };
      }),
    );
  };

  const validateForm = () => {
    const hasAudio = questionAudio.file || hintAudio.file || options.some(option => option.audioFile);
    if (hasAudio && !voiceName) {
      setFieldErrors({ voiceName: "Choose a voice actor for the attached audio." });
      return false;
    }
    // Drafts may omit content, but attached recordings always require an actor.
    if (courseData.isDraft || courseData.isDeleted) {
      setFieldErrors({});
      return true;
    }

    const nextErrors = {};
    if (!questionType) {
      nextErrors.questionType = "Select a question type.";
    }
    if (!question.trim()) {
      nextErrors.question = "Question text is required.";
    }

    const QUESTION_AUDIO_REQUIRED = new Set(["ACIQ", "CT", "ACIQM", "CTA"]);

    if (QUESTION_TYPES_WITH_OPTIONS.has(questionType)) {
      const filledOptions = options.filter((option) => option.text.trim());
      if (filledOptions.length < 2 && questionType !== "ACMC") {
        nextErrors.options = "Provide at least two options.";
      }
      const selectedOptions = options.filter((option) => option.correct);
      if (!selectedOptions.length) {
        nextErrors.options = "Select at least one correct option.";
      }
      if (SINGLE_ANSWER_TYPES.has(questionType) && selectedOptions.length > 1) {
        nextErrors.options = "Only one option can be marked correct.";
      }
      if (questionType === "ACMC") {
        const hasAudioForEach = options.every(
          (option) => option.text.trim() && option.audioFile,
        );
        if (!hasAudioForEach) {
          nextErrors.options = "Provide audio for each ACMC option.";
        }
      }
    }

    if (QUESTION_AUDIO_REQUIRED.has(questionType) && !questionAudio.file) {
      nextErrors.questionAudio = "Upload question audio";
    }

    if (["UVI", "LAR"].includes(questionType)) {
      if (!phrase.trim()) {
        nextErrors.phrase = "Phrase is required";
      }
      if (questionType === "LAR" && hintType !== "audio") {
        nextErrors.hintType = "Hint type must be audio for LAR questions.";
      }
    }

    if (hintType === "text" && !hint.trim()) {
      nextErrors.hint = "Hint text is required.";
    }

    if (hintType === "audio" && !hintAudio.file) {
      nextErrors.hintAudio = "Upload a hint audio file.";
    }

    setFieldErrors(nextErrors);
    return !Object.keys(nextErrors).length;
  };

  const buildAnswer = useCallback(() => {
    const selected = options.filter((option) => option.correct);
    if (!selected.length) {
      return "";
    }
    if (questionType === "ACMC") {
      return selected[0]?.text || "";
    }
    if (MULTI_ANSWER_TYPES.has(questionType)) {
      return selected.map((option) => option.text).join("; ");
    }
    return selected[0]?.text || "";
  }, [options, questionType]);

  const buildPayload = useCallback(() => {
    const answer = buildAnswer();
    const trimmedOptions = options
      .filter((option) => option.text.trim())
      .map((option) => ({
        ...option,
        text: option.text.trim(),
      }));

    let optionsForPayload = null;
    if (QUESTION_TYPES_WITH_OPTIONS.has(questionType)) {
      if (questionType === "ACMC") {
        optionsForPayload = trimmedOptions.reduce((acc, option) => {
          acc[option.text] = "";
          return acc;
        }, {});
      } else {
        optionsForPayload = trimmedOptions.map((option) => option.text);
      }
    }

    const normalizedHintType = hintType === "none" ? null : hintType;

    const payload = {
      voiceName: voiceName || undefined,
      order: nextOrder,
      questionType,
      question: question.trim(),
      Answer: answer,
      options: optionsForPayload,
      noteBrief: noteBrief.trim(),
      noteFull: noteFull.trim(),
      parentUnitId: unitId,
      parentCourseId: courseData.courseId,
      parentLevelId: levelId,
      hintType: normalizedHintType,
      hint: hintType === "text" ? hint.trim() : undefined,
      hintUrl: hintType === "audio" ? "" : undefined,
      phrase: ["UVI", "LAR"].includes(questionType) ? phrase.trim() : undefined,
      isQuestionArchived: false,
      url: ["ACIQ", "CTA", "CT", "ACIQM"].includes(questionType)
        ? ""
        : undefined,
    };

    Object.keys(payload).forEach((key) => {
      if (payload[key] === undefined) {
        delete payload[key];
      }
    });

    if (!QUESTION_TYPES_WITH_OPTIONS.has(questionType)) {
      payload.options = null;
    }

    const attachments = {
      questionAudioFile: questionAudio.file || null,
      hintAudioFile: hintAudio.file || null,
      optionAudioFiles:
        questionType === "ACMC"
          ? trimmedOptions.map((option) => ({
              text: option.text,
              file: option.audioFile,
            }))
          : [],
    };

    return { payload, attachments };
  }, [
    courseData,
    unitId,
    levelId,
    nextOrder,
    questionType,
    question,
    noteBrief,
    noteFull,
    phrase,
    hintType,
    hint,
    options,
    questionAudio,
    hintAudio,
    buildAnswer,
    voiceName,
  ]);

  const handleOnClick = async () => {
    setIsSubmitting(true);
    resetForm();
    setIsSubmitting(false);
  };

  const resetForm = () => {
    setQuestionType("");
    setQuestion("");
    setNoteBrief("");
    setNoteFull("");
    setPhrase("");
    setHintType("none");
    setHint("");
    setOptions([]);
    resetQuestionAudio();
    resetHintAudio();
    setFieldErrors({});
  };

  useEffect(() => {
    setOptions(getInitialOptions(questionType));
    setFieldErrors((prev) => ({ ...prev, options: undefined }));
  }, [questionType]);

  useEffect(() => {
    if (!hintTypes.includes(hintType)) {
      setHintType("none");
      setHint("");
      resetHintAudio();
      return;
    }

    if (hintType !== "text") {
      setHint("");
    }

    if (hintType !== "audio") {
      resetHintAudio();
    }

    if (questionType === "LAR" && hintType !== "audio") {
      setHintType("audio");
    } 
  }, [hintType, questionType]);

  useEffect(() => {
    if (!["UVI", "LAR"].includes(questionType)) {
      setPhrase("");
    }
  }, [questionType]);

  useEffect(() => {
    if (!onChange) {
      return;
    }
    const { payload, attachments } = buildPayload();
    onChange({ payload, attachments });
  }, [
    onChange,
    courseData,
    unitId,
    levelId,
    levelOrder,
    nextOrder,
    questionType,
    question,
    noteBrief,
    noteFull,
    phrase,
    hintType,
    hint,
    options,
    questionAudio,
    hintAudio,
    buildPayload,
  ]);

  const questionLabel = useMemo(() => {
    if (levelOrder && nextOrder) {
      return `L${levelOrder} • Q${nextOrder}`;
    }
    if (nextOrder) {
      return `Question ${nextOrder}`;
    }
    return "Question";
  }, [levelOrder, nextOrder]);

  useImperativeHandle(ref, () => ({
    validate: () => validateForm(),
    buildPayload: () => buildPayload(),
    validateAndBuild: () => {
      const isValid = validateForm();
      if (!isValid) {
        return null;
      }
      return buildPayload();
    },
  }));

  return (
    <Paper sx={{ padding: 2, marginTop: 2, width: "100%" }} elevation={3}>
      <Typography sx={{ mt: 2, mb: 2 }} variant="h6">
        {questionLabel}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <VoiceActorSelector courseId={courseData.courseId} value={voiceName} onChange={setVoiceName} />
        {fieldErrors.voiceName && <Typography color="error" variant="caption">{fieldErrors.voiceName}</Typography>}
      </Box>
      <Grid2 container spacing={2}>
        <Grid2 item size={{ xs: 6, md: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Question Type</InputLabel>
            <Select
              value={questionType}
              label="Question Type"
              onChange={(e) => setQuestionType(e.target.value)}
            >
              {questionTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {QUESTION_TYPE_MAPPING[type]}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {fieldErrors.questionType && (
            <Typography color="error" variant="caption">
              {fieldErrors.questionType}
            </Typography>
          )}
        </Grid2>
        <Grid2 item size={{ xs: 6, md: 8 }}>
          <TextField
            label="Question"
            variant="outlined"
            fullWidth
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          />
          {fieldErrors.question && (
            <Typography color="error" variant="caption">
              {fieldErrors.question}
            </Typography>
          )}
        </Grid2>

        {["ACIQ", "CTA", "CT", "ACIQM"].includes(questionType) && (
          <div className="audio-container">
            {questionAudio.preview ? (
              <AudioPlayerComponent audioUrl={questionAudio.preview} />
            ) : (
              <Typography variant="caption" sx={{ ml: 4 }}>
                No audio selected
              </Typography>
            )}
            <form
              style={{ marginLeft: "20px" }}
              // onSubmit={(event) => handleAudioSubmit(event, "questionAudio")}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <Button variant="outlined" component="label" color="info">
                  Choose File
                  <input
                    hidden
                    type="file"
                    accept=".mp3"
                    ref={questionAudioInputRef}
                    onChange={(event) =>
                      handleQuestionAudioChange(event.target.files?.[0] || null)
                    }
                  />
                </Button>
                {/* <Button type="submit" variant="contained" sx={{ ml: 2 }}>
                  Upload
                </Button> */}
              </Box>
            </form>
            {fieldErrors.questionAudio && (
              <Typography color="error" variant="caption">
                {fieldErrors.questionAudio}
              </Typography>
            )}
          </div>
        )}

        {/* {console.log("question options ----", question.Answer)} */}

        {!["ACMC", "UVI", "CT", "LAR", "CTA"].includes(questionType) && (
          <>
            <Grid2 item size={{ xs: 12 }}>
              <Table sx={{ background: "primary.lighterShade" }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Option Text</TableCell>
                    <TableCell align="right">Is Correct Answer</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {options &&
                    options.map((option, index) => (
                      <TableRow key={index}>
                        <TableCell>
                          <TextField
                            value={option.text}
                            fullWidth
                            onChange={(e) =>
                              handleOptionTextChange(index, e.target.value)
                            }
                          />
                        </TableCell>
                        <TableCell style={{ width: 160 }} align="center">
                          <Checkbox
                            checked={option.correct}
                            onChange={(e) =>
                              handleOptionCorrectChange(index, e.target.checked)
                            }
                          />
                        </TableCell>
                        <TableCell style={{ width: 160 }} align="right">
                          <Button
                            variant="contained"
                            color="secondary"
                            sx={{ mt: 2 }}
                            onClick={() => handleRemoveOption(index)}
                          >
                            Remove
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
              <Button
                variant="contained"
                color="info"
                sx={{ mt: 2 }}
                onClick={handleAddOption}
              >
                Add Option
              </Button>
            </Grid2>
            {fieldErrors.options && (
              <Grid2 item size={{ xs: 12 }}>
                <Typography color="error" variant="caption">
                  {fieldErrors.options}
                </Typography>
              </Grid2>
            )}
          </>
        )}

        {questionType === "ACMC" ? (
          <>
            <Grid2 item size={{ xs: 12 }}>
              <Table sx={{ background: "primary.lighterShade" }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Option</TableCell>
                    <TableCell>Audio Clip</TableCell>
                    <TableCell align="center">Is Correct Answer</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {options.map((option, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {option.text}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center" }}>
                          {option.audioUrl ? (
                            <AudioPlayerComponent audioUrl={option.audioUrl} />
                          ) : (
                            <Typography variant="body2" sx={{ mr: 2 }}>
                              No audio selected
                            </Typography>
                          )}
                          <Button component="label" variant="outlined">
                            Upload
                            <input
                              hidden
                              type="file"
                              accept=".mp3"
                              onChange={(event) => {
                                const file = event.target.files?.[0] || null;
                                handleOptionAudioChange(index, file);
                              }}
                            />
                          </Button>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Checkbox
                          checked={option.correct}
                          onChange={(e) =>
                            handleOptionCorrectChange(index, e.target.checked)
                          }
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Button
                          variant="contained"
                          color="secondary"
                          sx={{ mt: 2 }}
                          onClick={() => handleRemoveOption(index)}
                        >
                          Remove
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              <Button
                variant="contained"
                color="info"
                sx={{ mt: 2 }}
                onClick={handleAddOption}
              >
                Add Option
              </Button>
            </Grid2>
            {fieldErrors.options && (
              <Grid2 item size={{ xs: 12 }}>
                <Typography color="error" variant="caption">
                  {fieldErrors.options}
                </Typography>
              </Grid2>
            )}
          </>
        ) : null}

        <Grid2 item size={{ xs: 6, md: 6 }}>
          <TextField
            label="Note Brief"
            variant="outlined"
            fullWidth
            value={noteBrief}
            onChange={(e) => setNoteBrief(e.target.value)}
          />
        </Grid2>

        <Grid2 item size={{ xs: 6, md: 6 }}>
          <TextField
            label="Note Full"
            variant="outlined"
            fullWidth
            value={noteFull}
            onChange={(e) => setNoteFull(e.target.value)}
          />
        </Grid2>

        <Grid2 item size={{ xs: 6, md: 4 }}>
          <FormControl fullWidth>
            <InputLabel>Hint Type</InputLabel>
            <Select
              label="Hint Type"
              value={hintType}
              onChange={(e) => setHintType(e.target.value)}
            >
              {hintTypes.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid2>

        {(questionType === "UVI" || questionType === "LAR") && (
          <Grid2 item size={{ xs: 6, md: 4 }}>
            <TextField
              label="Phrase"
              variant="outlined"
              fullWidth
              value={phrase}
              onChange={(e) => setPhrase(e.target.value)}
            />
            {fieldErrors.phrase && (
              <Typography color="error" variant="caption">
                {fieldErrors.phrase}
              </Typography>
            )}
          </Grid2>
        )}

        {hintType === "text" && (
          <Grid2 item size={{ xs: 6, md: 8 }}>
            <TextField
              label="Hint"
              variant="outlined"
              fullWidth
              value={hint}
              onChange={(e) => setHint(e.target.value)}
            />
            {fieldErrors.hint && (
              <Typography color="error" variant="caption">
                {fieldErrors.hint}
              </Typography>
            )}
          </Grid2>
        )}

        {hintType === "audio" ? (
          <div>
            <Grid2 item size={{ xs: 6, md: 8 }}>
              <InputLabel sx={{ marginLeft: 2, marginTop: 1 }}>
                Hint Audio
              </InputLabel>
            </Grid2>
            <div className="audio-container">
              {hintAudio.preview ? (
                <AudioPlayerComponent audioUrl={hintAudio.preview} />
              ) : (
                <Typography variant="caption" sx={{ ml: 4 }}>
                  No audio selected
                </Typography>
              )}
              <form
                style={{ marginLeft: "20px" }}
                // onSubmit={(event) => handleAudioSubmit(event, "hintAudio")}
              >
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  <Button variant="outlined" component="label" color="info">
                    Choose File
                    <input
                      hidden
                      type="file"
                      accept=".mp3"
                      ref={hintAudioInputRef}
                      onChange={(event) =>
                        handleHintAudioChange(event.target.files?.[0] || null)
                      }
                    />
                  </Button>
                  {/* <Button type="submit" variant="contained" sx={{ ml: 2 }}>
                    Upload
                  </Button> */}
                </Box>
              </form>
            </div>
            {fieldErrors.hintAudio && (
              <Typography color="error" variant="caption">
                {fieldErrors.hintAudio}
              </Typography>
            )}
          </div>
        ) : (
          ""
        )}
        <br />
        <Grid2
          item
          size={{ xs: 12 }}
          display="flex"
          justifyContent="flex-end"
          gap={2}
        >
          <Button
            variant="outlined"
            color="secondary"
            onClick={() => {
              resetForm();
              onCancel?.();
            }}
          >
            {secondaryBtnText}
          </Button>
          {displayConfirmButton && (
            <Button
              variant="contained"
              color="primary"
              disabled={isSubmitting}
              onClick={handleOnClick}
            >
              {isSubmitting ? "Saving..." : "Save Question"}
            </Button>
          )}
        </Grid2>
        {formMessage && (
          <Grid2 item size={{ xs: 12 }}>
            <Typography
              variant="body2"
              color={
                formMessage.type === "error"
                  ? "error"
                  : formMessage.type === "warning"
                    ? "warning.main"
                    : "success.main"
              }
              sx={{ mt: 1 }}
            >
              {formMessage.text}
            </Typography>
          </Grid2>
        )}
      </Grid2>
      {/* ))} */}
    </Paper>
  );
});

NewQuestionForm.propTypes = {
  courseData: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
    isDeleted: PropTypes.bool.isRequired,
    isDraft: PropTypes.bool.isRequired,
  }).isRequired,
  unitId: PropTypes.string.isRequired,
  levelId: PropTypes.string,
  levelOrder: PropTypes.number,
  nextOrder: PropTypes.number,
  onCancel: PropTypes.func,
  onSuccess: PropTypes.func,
  onChange: PropTypes.func,
  displayConfirmButton: PropTypes.bool,
  secondaryBtnText: PropTypes.string,
};

NewQuestionForm.defaultProps = {
  levelId: undefined,
  levelOrder: undefined,
  nextOrder: undefined,
  onCancel: undefined,
  onSuccess: undefined,
  onChange: undefined,
  displayConfirmButton: undefined,
  secondaryBtnText: "Cancel",
};

export default NewQuestionForm;
